import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import BookingModal from './components/BookingModal';
import CancelBooking from './components/CancelBooking';
import { authenticateDriver } from './config/drivers';
import Home from './pages/Home';
import Book from './pages/Book';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Cancellation from './pages/Cancellation';
import DriverPortal from './pages/DriverPortal';
import BusinessPortal from './pages/BusinessPortal';

import { initPhoneClickTracking } from './utils/gtag';
import { fetchPricingConfig, getBundledConfig } from './utils/pricingConfigClient';
import CookieConsent from './components/CookieConsent';
import Popup from './components/Popup';

// Admin Panel (Section 6) — statically imports the 45MB oil-specs DB, so
// lazy-load it; that weight lands in an admin-only chunk instead of the main
// (public) bundle.
const AdminApp = lazy(() => import('./admin/AdminApp'));

function AdminLoginModal({ onLogin, onClose, error }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(username, password);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="admin-login-modal" onClick={e => e.stopPropagation()}>
        <button className="admin-login-close" onClick={onClose}>&times;</button>
        <h3>Admin Login</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoFocus
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {error && <p className="admin-login-error">{error}</p>}
          <button type="submit">Login</button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [services, setServices] = useState(getBundledConfig().display_services || []);
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState('');
  const location = useLocation();

  // Restore admin session from localStorage
  useEffect(() => {
    const savedAdmin = localStorage.getItem('adminAuth');
    if (savedAdmin === 'true') setIsAdmin(true);
  }, []);

  // Initialize Google Ads phone click tracking
  useEffect(() => {
    initPhoneClickTracking();
  }, []);

  // Load latest services pricing from admin overrides
  useEffect(() => {
    let cancelled = false;
    fetchPricingConfig().then(cfg => {
      if (!cancelled && Array.isArray(cfg.display_services)) {
        setServices(cfg.display_services);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleAdminLogin = (username, password) => {
    const driver = authenticateDriver(username, password);
    if (driver && driver.role === 'admin') {
      setIsAdmin(true);
      setShowAdminLogin(false);
      setAdminLoginError('');
      localStorage.setItem('adminAuth', 'true');
    } else {
      setAdminLoginError('Invalid credentials');
    }
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    localStorage.removeItem('adminAuth');
  };

  // Check if we're in admin section, driver portal, or business portal
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isDriverRoute = location.pathname.startsWith('/driver');
  const isBusinessRoute = location.pathname.startsWith('/business');

  const handleBookNow = (service) => {
    setSelectedService(service);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedService(null);
  };

  // Render admin panel separately (no header/footer)
  if (isAdminRoute) {
    return (
      <Suspense fallback={<div style={{ padding: 40 }}>Loading admin…</div>}>
        <Routes>
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </Suspense>
    );
  }

  // Render driver portal separately (no header/footer)
  if (isDriverRoute) {
    return (
      <Routes>
        <Route path="/driver" element={<DriverPortal />} />
      </Routes>
    );
  }

  // Render business portal separately (no header/footer)
  if (isBusinessRoute) {
    return (
      <Routes>
        <Route path="/business" element={<BusinessPortal />} />
      </Routes>
    );
  }

  return (
    <div className="App">
      <Header />

      <Routes>
        <Route path="/" element={<Home services={services} onBookNow={handleBookNow} />} />
        <Route path="/schedule-now" element={<Home services={services} onBookNow={handleBookNow} />} />
        <Route path="/book" element={<Book services={services} />} />
        <Route path="/book/:category" element={<Book services={services} />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cancellation" element={<Cancellation />} />
      </Routes>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <h3>NORCAL MOBILE OIL CHANGE</h3>
              <p>Mobile Oil Change Service</p>
              <p className="footer-tagline">We Come To You!</p>
            </div>

            <div className="footer-contact">
              <h4>Contact</h4>
              <p>
                <a
                  href="tel:+19168188494"
                  className="footer-action-link"
                  data-event="cta_call_click"
                  data-cta-location="footer"
                >📞 Call Us</a>
              </p>
              <p>
                <a
                  href="sms:+19168188494"
                  className="footer-action-link"
                  data-event="cta_sms_click"
                  data-cta-location="footer"
                >💬 Text Us</a>
              </p>
              <p>
                <a href="mailto:info@norcalmobileoilchange.com">✉️ info@norcalmobileoilchange.com</a>
              </p>
              <p>📍 Sacramento, California</p>
            </div>

            <div className="footer-links">
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><button className="link-button" onClick={() => setShowCancelModal(true)}>Cancel Booking</button></li>
              </ul>
            </div>

            <div className="footer-legal">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/cancellation">Cancellation Policy</Link></li>
                <li>
                  {isAdmin ? (
                    <button className="link-button admin-link" onClick={handleAdminLogout}>Admin ✓ (Logout)</button>
                  ) : (
                    <button className="link-button admin-link" onClick={() => setShowAdminLogin(true)}>Admin Login</button>
                  )}
                </li>
                <li><Link to="/driver" className="driver-link">Driver Portal</Link></li>
                <li><Link to="/business" className="driver-link">Business Portal</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-badges">
              <span className="badge">✓ Licensed & Insured</span>
              <span className="badge">✓ 7-Day Guarantee</span>
              <span className="badge">✓ Full Synthetic Oil</span>
            </div>
            <p className="footer-copyright">
              © {new Date().getFullYear()} NorCal Mobile Oil Change. All rights reserved. Sacramento, California.
            </p>
          </div>
        </div>
      </footer>

      {showModal && (
        <BookingModal
          service={selectedService}
          onClose={closeModal}
          isAdmin={isAdmin}
        />
      )}

      {showCancelModal && (
        <CancelBooking onClose={() => setShowCancelModal(false)} />
      )}

      {showAdminLogin && (
        <AdminLoginModal
          onLogin={handleAdminLogin}
          onClose={() => { setShowAdminLogin(false); setAdminLoginError(''); }}
          error={adminLoginError}
        />
      )}

      <Popup />
      <CookieConsent />
    </div>
  );
}

export default App;
